/// <amd-module name="Build/Admin/Pool/PoolSignalRHub" />
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";

import { TaskAgentStatus } from "DistributedTask/Client/Contracts/TaskAgent";
import { HubNames, IPoolHubServer, IPoolHubClient, getDTProxyToRegister } from "DistributedTask/Common/SignalR/SignalRHubProxies";

import { AgentActions } from "Build/Admin/Common/Actions/AgentActions";
import { ISignalRHubOptionsBase, SignalRHubBase } from "Build/Admin/Common/SignalRHubBase";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";

export interface IPoolSignalRHubOptions extends ISignalRHubOptionsBase {
    actionCreator: PoolActionCreator;
    agentActions: AgentActions;
}

// We create a wrapper class around the hub implementation and make the actual hub a static member of the wrapper class
// to enforce the fact that there should only be one active hub on a particular view at any given time.
export class PoolSignalRHub {
    private static hub: PoolSignalRHubImpl | undefined;

    constructor(options: IPoolSignalRHubOptions) {
        if (!PoolSignalRHub.hub) {
            PoolSignalRHub.hub = new PoolSignalRHubImpl(options);
        }
    }

    public listen(poolId: number): void {
        if (PoolSignalRHub.hub) {
            PoolSignalRHub.hub.listen(poolId);
        }
    }

    public dispose(): void {
        if (PoolSignalRHub.hub) {
            PoolSignalRHub.hub.dispose();
            PoolSignalRHub.hub = undefined;
        }
    }
}

class PoolSignalRHubImpl extends SignalRHubBase<IPoolHubClient, IPoolHubServer, IPoolSignalRHubOptions> {
    private _poolId: number | undefined;

    constructor(options: IPoolSignalRHubOptions) {
        super(HubNames.poolHub, options);

        this.initializeProxy(getDTProxyToRegister, this._initializeClient);
        this.addListener(this);
    }

    public listen(poolId: number): void {
        this._poolId = poolId;
        this.listenTimer = window.setTimeout(
            () => {
                this.subscribePromise = this.start().then(() => {
                    const hub = this.hub;
                    if (hub && this._poolId) {
                         hub.server.subscribe(this._poolId);
                    }
                });
            },
            1000 // run on a brief timeout, to avoid quick switching
        );
    }

    public dispose(): void {
        this.clearTimer();

        const hub = this.hub;
        // If we never called .start() then we don't need to call super.dispose()
        if (hub && this.subscribePromise) {
            this.subscribePromise.then(() => {
                if (this._poolId) {
                    hub.server.unsubscribe(this._poolId)
                        .then(() => super.dispose(), () => super.dispose());
                }
            });
        }
    }

    private _initializeClient = (proxy: ISignalRHubProxy<IPoolHubClient, IPoolHubServer>) => {
        proxy.client.notifyAgentAdded = (poolId: number, agentId: number) => {
            this.options.actionCreator.getAgentData(agentId, poolId);
        };
        proxy.client.notifyAgentConnected = (poolId: number, agentId: number) => {
            this.options.agentActions.agentStatusUpdated.invoke({ agentId, status: TaskAgentStatus.Online });
        };
        proxy.client.notifyAgentDisconnected = (poolId: number, agentId: number) => {
            this.options.agentActions.agentStatusUpdated.invoke({ agentId, status: TaskAgentStatus.Offline });
        };
        proxy.client.notifyAgentUpdated = (poolId: number, agentId: number) => {
            this.options.actionCreator.getAgentData(agentId, poolId);
        };
        proxy.client.notifyAgentRequestAssigned = (poolId: number, requestId: number) => {
            this.options.actionCreator.getRequestData(poolId, requestId);
        };
        proxy.client.notifyAgentRequestQueued = (poolId: number, requestId: number) => {
            this.options.actionCreator.getRequestData(poolId, requestId);
        };
        proxy.client.notifyAgentRequestStarted = (poolId: number, requestId: number) => {
            this.options.actionCreator.getRequestData(poolId, requestId);
        };
        proxy.client.notifyAgentRequestCompleted = (poolId: number, requestId: number) => {
            this.options.actionCreator.getRequestData(poolId, requestId);
        };
    }
}
